import { useCallback, useMemo, useState } from 'react';
import type { Row } from '@tanstack/react-table';
import type { Delivery } from '@/services/db/delivery/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { buildDeliveryBulkActions } from '../utils/bulk-actions';
import { isDeliverySelectable } from '../utils/selection';
import { ALL_DELIVERY_CHANNEL_VALUES } from '../utils/state-options';
import { createDeliveryColumns } from './delivery-table-columns';
import { DeliveryEmptyState } from './delivery-empty-state';
import { DeliveryRowExpansion } from './delivery-row-expansion';
import type { DeliveryTableProps } from './delivery-table';

type DeliveryExpandedRowProps = { row: Row<Delivery> };

/**
 * Builds the memoized `DataTable` inputs for `DeliveryTable` — applying the
 * prop defaults and producing the row data, columns, controlled selection,
 * bulk actions, selectability predicate, load-more config, and empty-state
 * renderer. Extracting this keeps `DeliveryTable` a trivially thin render.
 */
export function useDeliveryTable(props: DeliveryTableProps) {
  const taxonomy = useProductTaxonomy();
  const {
    deliveries,
    getDeliveryHref,
    channels = ALL_DELIVERY_CHANNEL_VALUES,
    actionType = '',
    recipientHeader = `Recipient / ${taxonomy.term('people.titleSingular')}`,
    hasReadOnlyPermission = false,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    onBulkRetry,
    onBulkSend,
    onBulkDelete,
    onBulkRedeliver,
    onRowRetry,
    onRowDelete,
    emptyMessage,
    hasFilters,
    isDrafted,
    withIndividualPersonBlankSlate,
  } = props;

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // DataTable wants a mutable array; the query returns readonly.
  const data = useMemo(() => [...deliveries], [deliveries]);

  // Prune the controlled selection to rows still present. A filter change /
  // refetch replaces the list, and Pluma's selected-count header tallies the raw
  // selection map — so stale ids would otherwise show a phantom "N selected".
  // Load-more is safe: appended rows keep their ids, so selected ids survive.
  const rowIdsKey = data.map((delivery) => delivery.id).join(',');
  const [prevRowIdsKey, setPrevRowIdsKey] = useState(rowIdsKey);
  if (rowIdsKey !== prevRowIdsKey) {
    setPrevRowIdsKey(rowIdsKey);
    setRowSelection((prev) => {
      const pruned: Record<string, boolean> = {};
      for (const delivery of data) if (prev[delivery.id] === true) pruned[delivery.id] = true;
      return Object.keys(pruned).length === Object.keys(prev).length ? prev : pruned;
    });
  }

  const columns = useMemo(
    () => createDeliveryColumns({ channels, actionType, getDeliveryHref, recipientHeader }),
    [channels, actionType, getDeliveryHref, recipientHeader],
  );

  const selected = useMemo(() => data.filter((delivery) => rowSelection[delivery.id] === true), [data, rowSelection]);
  const bulkActions = useMemo(
    () => ({
      promotedActions: buildDeliveryBulkActions(data, selected, {
        hasReadOnlyPermission,
        onBulkRetry,
        onBulkSend,
        onBulkDelete,
      }),
    }),
    [data, selected, hasReadOnlyPermission, onBulkRetry, onBulkSend, onBulkDelete],
  );

  const getRowCanSelect = useCallback(
    (row: Row<Delivery>) => !hasReadOnlyPermission && isDeliverySelectable(row.original),
    [hasReadOnlyPermission],
  );

  // The table renders `expandedRowComponent` with only `{ row }`, so the row
  // action callbacks are closed over here and bound to the row's id.
  const ExpandedRowComponent = useMemo(() => {
    function DeliveryExpandedRow({ row }: DeliveryExpandedRowProps) {
      const delivery = row.original;
      return (
        <DeliveryRowExpansion
          delivery={delivery}
          onSend={() => onBulkSend?.([delivery.id])}
          onDelete={() => onRowDelete?.([delivery.id])}
          onRetry={() => onRowRetry?.([delivery.id])}
          onRedeliver={(tracked) => onBulkRedeliver?.([delivery.id], { tracked })}
        />
      );
    }
    return DeliveryExpandedRow;
  }, [onBulkSend, onRowDelete, onRowRetry, onBulkRedeliver]);

  const loadMore = useMemo(
    () => ({
      canLoad: hasMore,
      isLoading: isLoadingMore,
      label: 'Load more deliveries',
      onClick: () => onLoadMore?.(),
    }),
    [hasMore, isLoadingMore, onLoadMore],
  );

  const EmptyStateComponent = useCallback(
    () => (
      <DeliveryEmptyState
        emptyMessage={emptyMessage}
        hasFilters={hasFilters}
        isDrafted={isDrafted}
        withIndividualPersonBlankSlate={withIndividualPersonBlankSlate}
      />
    ),
    [emptyMessage, hasFilters, isDrafted, withIndividualPersonBlankSlate],
  );

  return {
    data,
    columns,
    rowSelection,
    setRowSelection,
    bulkActions,
    getRowCanSelect,
    ExpandedRowComponent,
    loadMore,
    EmptyStateComponent,
  };
}
